import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import './QRScanner.css';
import { useLanguage } from '../context/LanguageContext';

// ─── Only scan the formats we actually need.
// Scanning all formats (the default) is much slower because the engine
// tries every decoder on every frame.
const SCAN_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
];

// ─── Build the scanner config once (not inside render).
// Key changes vs. original:
//   • fps 10 → 15   – more frames per second = faster recognition
//   • qrbox function – adapts to viewport; wider aspect for EAN barcodes
//   • disableFlip true – QR/EAN don't need mirror-checking; halves CPU per frame
//   • formatsToSupport – only QR + EAN decoders run per frame
//   • experimentalFeatures – enables the native BarcodeDetector API on
//     Chrome/Android/Edge, which is 3–5× faster than the JS fallback
const buildScanConfig = () => ({
  fps: 15,
  qrbox: (viewfinderWidth, viewfinderHeight) => {
    // For QR codes a square box is ideal; for EAN we want wider.
    // We use a rectangle that works reasonably for both.
    const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
    const w = Math.floor(Math.min(minEdge * 0.85, 300));
    const h = Math.floor(w * 0.65); // ~65 % height covers EAN aspect ratio
    return { width: w, height: h };
  },
  disableFlip: true,
  formatsToSupport: SCAN_FORMATS,
  experimentalFeatures: {
    // Uses the native BarcodeDetector API where available (Chrome, Android
    // WebView, Edge).  Falls back to the JS decoder on iOS Safari automatically.
    useBarCodeDetectorIfSupported: true,
  },
});

const QRScanner = ({ isOpen, onClose, onScan }) => {
  const { t } = useLanguage();
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoom] = useState(1);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaTrackRef = useRef(null);
  const streamRef = useRef(null);

  const getMediaTrack = useCallback(async () => {
    try {
      if (scannerRef.current) {
        const videoElement = document.querySelector('#qr-reader video');
        if (videoElement && videoElement.srcObject) {
          const stream = videoElement.srcObject;
          streamRef.current = stream;
          const tracks = stream.getVideoTracks();
          if (tracks.length > 0) {
            mediaTrackRef.current = tracks[0];
            return mediaTrackRef.current;
          }
        }
      }
    } catch (err) {
      console.warn('Error getting media track:', err);
    }
    return null;
  }, []);

  const applyTorch = useCallback(async (enabled) => {
    try {
      const track = await getMediaTrack();
      if (!track) return;
      const capabilities = track.getCapabilities();
      if (capabilities?.torch) {
        await track.applyConstraints({ advanced: [{ torch: enabled }] });
        setTorchOn(enabled);
      }
    } catch (err) {
      console.warn('Error applying torch:', err);
    }
  }, [getMediaTrack]);

  const applyZoom = useCallback(async (value) => {
    try {
      const track = await getMediaTrack();
      if (!track) return;
      const capabilities = track.getCapabilities();
      if (capabilities?.zoom) {
        const clamped = Math.min(capabilities.zoom.max, Math.max(capabilities.zoom.min, value));
        await track.applyConstraints({ advanced: [{ zoom: clamped }] });
        setZoom(clamped);
      } else {
        setZoom(value);
      }
    } catch (err) {
      console.warn('Error applying zoom:', err);
    }
  }, [getMediaTrack]);

  const cleanupScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (e) {
        console.warn('Error during scanner cleanup:', e);
      }
      scannerRef.current = null;
    }
    const el = document.getElementById('qr-reader');
    if (el) el.innerHTML = '';
    mediaTrackRef.current = null;
    streamRef.current = null;
    setIsScanning(false);
  }, []);

  const handleClose = useCallback(() => {
    cleanupScanner();
    setError(null);
    setTorchOn(false);
    setZoom(1);
    try { document.body.classList.remove('qr-scroll-lock'); } catch (_) {}
    onClose();
  }, [cleanupScanner, onClose]);

  // ─── Resolve the best camera deviceId for the requested facingMode.
  // Falls back to a facingMode constraint directly (works better on some iOS
  // devices that don't expose readable camera labels).
  const resolveCameraConstraint = useCallback(async (requestedFacingMode) => {
    try {
      const devices = await Html5Qrcode.getCameras();
      if (!devices || devices.length === 0) return { facingMode: requestedFacingMode };

      if (requestedFacingMode === 'environment') {
        const back = devices.find(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment') ||
          (!d.label.toLowerCase().includes('front') && !d.label.toLowerCase().includes('user'))
        );
        if (back) return { deviceId: back.id };
      } else {
        const front = devices.find(d =>
          d.label.toLowerCase().includes('front') ||
          d.label.toLowerCase().includes('user')
        );
        if (front) return { deviceId: front.id };
      }

      // No label match – use facingMode so the browser picks the right lens
      return { facingMode: requestedFacingMode };
    } catch {
      return { facingMode: requestedFacingMode };
    }
  }, []);

  // ─── Start or restart the scanner
  const startScanner = useCallback(async (requestedFacingMode) => {
    setError(null);
    setIsScanning(true);

    try {
      // Clean up any previous instance first
      const qrReaderEl = document.getElementById('qr-reader');
      if (qrReaderEl) qrReaderEl.innerHTML = '';

      // NOTE: We intentionally do NOT pre-open the camera for a permission
      // check and immediately stop it. That double open/close adds 300–800 ms
      // of latency. Instead we let html5-qrcode request permission inline.

      const html5QrCode = new Html5Qrcode('qr-reader');
      const cameraConstraint = await resolveCameraConstraint(requestedFacingMode);

      await html5QrCode.start(
        cameraConstraint,
        buildScanConfig(),
        (decodedText) => {
          onScan(decodedText);
          handleClose();
        },
        (errorMessage) => {
          // These fire on every frame where no code is found – suppress them
          if (
            !errorMessage.includes('NotFound') &&
            !errorMessage.includes('NotAllowed') &&
            !errorMessage.includes('No MultiFormat Readers')
          ) {
            console.log('Scan frame error:', errorMessage);
          }
        }
      );

      scannerRef.current = html5QrCode;

      // Give the video stream a moment to stabilise, then grab the track for
      // torch/zoom control
      setTimeout(() => getMediaTrack(), 200);
    } catch (err) {
      console.error('QR Scanner error:', err);
      const msg = err?.message || '';
      if (msg.includes('NotAllowedError') || msg.includes('Permission denied')) {
        setError(new Error(t('cameraPermissionDenied') || 'Camera permission denied. Please allow camera access.'));
      } else if (msg.includes('NotFoundError') || msg.includes('No cameras found')) {
        setError(new Error(t('noCameraFound') || 'No camera found. Please check your device.'));
      } else if (msg.includes('NotSupportedError')) {
        setError(new Error(t('cameraNotSupported') || 'Camera not supported on this device.'));
      } else if (msg.includes('NotReadableError')) {
        setError(new Error(t('cameraInUse') || 'Camera is in use by another application.'));
      } else if (msg.includes('OverconstrainedError')) {
        setError(new Error(t('cameraConstraintsNotMet') || 'Camera constraints not met.'));
      } else {
        setError(new Error(`${t('failedToStartCamera') || 'Failed to start camera'}: ${msg}`));
      }
      setIsScanning(false);
    }
  }, [resolveCameraConstraint, getMediaTrack, onScan, handleClose, t]);

  // ─── Open / close lifecycle
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    try { document.body.classList.add('qr-scroll-lock'); } catch (_) {}

    // No setTimeout wrapper here – starting immediately is faster
    startScanner(facingMode);

    return () => {
      cleanupScanner();
      try { document.body.classList.remove('qr-scroll-lock'); } catch (_) {}
    };
    // facingMode intentionally NOT in deps – we handle camera switches explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ─── Poll for media track (needed for torch/zoom after camera starts)
  useEffect(() => {
    if (!isOpen || !isScanning) return;
    const interval = setInterval(async () => {
      if (!mediaTrackRef.current) await getMediaTrack();
    }, 300);
    return () => clearInterval(interval);
  }, [isOpen, isScanning, getMediaTrack]);

  // ─── Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupScanner();
      try { document.body.classList.remove('qr-scroll-lock'); } catch (_) {}
    };
  }, [cleanupScanner]);

  // ─── Switch front / back camera
  const toggleFacingMode = async () => {
    setError(null);
    setIsSwitchingCamera(true);
    setTorchOn(false);
    setZoom(1);

    try {
      await cleanupScanner();
      const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
      setFacingMode(newFacingMode);

      // Small delay so React flushes the state before we start the new scanner
      setTimeout(async () => {
        await startScanner(newFacingMode);
        setIsSwitchingCamera(false);
      }, 150);
    } catch (err) {
      setError(new Error(`${t('cameraSwitchFailed')}: ${err?.message || err}`));
      setIsSwitchingCamera(false);
    }
  };

  const onPickFromGallery = () => fileInputRef.current?.click();

  const handleFileSelected = async (event) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      const { Html5Qrcode: H } = await import('html5-qrcode');
      const reader = new H('file-reader');
      const result = await reader.scanFile(file, true);
      if (result) {
        onScan(result);
        handleClose();
      }
    } catch {
      setError(new Error(t('unableToReadCodeFromImage')));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-modal">
        <div className="qr-top">
          <button className="back-btn" onClick={handleClose} aria-label={t('close')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="topbar">
            <button className="topbar-btn" onClick={onPickFromGallery} aria-label={t('gallery')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M4 5H20V19H4V5Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M4 16L9 11L12 14L15 11L20 16" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
            <button className={`topbar-btn ${torchOn ? 'active' : ''}`} onClick={() => applyTorch(!torchOn)} aria-label={t('flash')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M7 2H17L10 13H14L7 22V13H3L7 2Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
            <button className="topbar-btn" onClick={toggleFacingMode} aria-label={t('switchCamera')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 7h-3l-2-2H9L7 7H4a2 2 0 00-2 2v7a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 15l-2-2 2-2M16 9l2 2-2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="qr-scanner-content" ref={containerRef}>
          {error ? (
            <div className="qr-scanner-error">
              <p>{t('errorAccessingCamera')}: {error.message}</p>
              <button onClick={() => startScanner(facingMode)}>{t('tryAgain')}</button>
            </div>
          ) : (
            <div className="qr-scanner-viewport">
              <div id="qr-reader"></div>
              <div id="file-reader" style={{ display: 'none' }}></div>

              {isSwitchingCamera && (
                <div className="qr-scanner-loading" style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.8)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', zIndex: 20
                }}>
                  <div className="loading-spinner"></div>
                  <p style={{ color: 'white', marginTop: '10px' }}>{t('switchingCamera')}</p>
                </div>
              )}

              <div className="qr-scanner-overlay-frame">
                <div className="qr-scanner-corner top-left" />
                <div className="qr-scanner-corner top-right" />
                <div className="qr-scanner-corner bottom-left" />
                <div className="qr-scanner-corner bottom-right" />
                <div className="scan-line" />
              </div>
            </div>
          )}
        </div>

        <div className="qr-scanner-bottom-ui">
          <div className="zoom-controls">
            <button className="zoom-btn" onClick={() => applyZoom(Math.max(1, zoom - 0.2))}>-</button>
            <input
              className="zoom-slider"
              type="range" min="1" max="5" step="0.1"
              value={zoom}
              onChange={(e) => applyZoom(parseFloat(e.target.value))}
            />
            <button className="zoom-btn" onClick={() => applyZoom(Math.min(5, zoom + 0.2))}>+</button>
          </div>
          <button className="capture-btn" aria-label={t('capture')}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M12 8a4 4 0 100 8 4 4 0 000-8Z" stroke="white" strokeWidth="2"/>
              <path d="M9 7l1-2h4l1 2h2a2 2 0 012 2v7a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2h2Z" stroke="white" strokeWidth="2"/>
            </svg>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
      </div>
    </div>,
    document.body
  );
};

export default QRScanner;
